import { useEffect, useState } from "react";
import { api } from "@/api/client";
import { fmtEur } from "@/lib/domain";
import { STATUS_META } from "@/lib/domain";
import { Card } from "@/components/ui/card";
import { StatusBadge } from "@/components/StatusBadge";
import { TrendingUp, TrendingDown, Wallet, Percent } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell,
} from "recharts";

export default function Finances() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/finances").then((r) => setData(r.data)).catch(() => setData({ objects: [], totals: {} }));
  }, []);

  if (!data) return <div className="text-slate-400">Загрузка…</div>;

  const chartData = (data.objects || []).map((o) => ({
    name: o.title.length > 20 ? o.title.slice(0, 20) + "…" : o.title,
    revenue: o.revenue_actual,
    cost: o.actual_cost,
    margin: o.margin,
  }));

  const totals = data.totals || {};

  return (
    <div className="space-y-6">
      <div>
        <div className="text-[11px] uppercase tracking-[0.3em] text-amber-400/80 mb-2 font-medium">Финансовый контроль</div>
        <h1 className="font-display font-extrabold text-3xl sm:text-4xl">Финансы</h1>
        <p className="text-slate-400 mt-1 text-sm">Прибыльность по объектам · маржа · оборот</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <TotalCard label="Оборот (факт)" value={fmtEur(totals.revenue_actual)} icon={TrendingUp} color="emerald" />
        <TotalCard label="Оборот (план)" value={fmtEur(totals.revenue_planned)} icon={Wallet} color="blue" />
        <TotalCard label="Затраты" value={fmtEur(totals.cost)} icon={TrendingDown} color="red" />
        <TotalCard label={`Маржа (${totals.margin_pct || 0}%)`} value={fmtEur(totals.margin)} icon={Percent} color="amber" />
      </div>

      <Card className="p-5 bg-[#131924] border-[#212b3d]">
        <h3 className="font-display font-semibold text-lg mb-4">Оборот, затраты и маржа по объектам</h3>
        <div style={{ width: "100%", height: 340 }}>
          <ResponsiveContainer>
            <BarChart data={chartData} margin={{ top: 10, right: 10, bottom: 40, left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#212b3d" />
              <XAxis dataKey="name" stroke="#64748b" fontSize={11} angle={-25} textAnchor="end" height={70} />
              <YAxis stroke="#64748b" fontSize={11} tickFormatter={(v) => (v / 1000).toFixed(0) + "k"} />
              <Tooltip
                contentStyle={{ backgroundColor: "#131924", border: "1px solid #212b3d", borderRadius: 8, fontSize: 12 }}
                labelStyle={{ color: "#f8fafc" }}
                formatter={(v) => fmtEur(v)}
              />
              <Bar dataKey="revenue" name="Оборот" fill="#10b981" radius={[4, 4, 0, 0]} />
              <Bar dataKey="cost" name="Затраты" fill="#ef4444" radius={[4, 4, 0, 0]} />
              <Bar dataKey="margin" name="Маржа" fill="#f59e0b" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <div className="rounded-xl border border-[#212b3d] bg-[#131924] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#0f1521] text-slate-400">
              <tr>
                <th className="text-left px-4 py-3 font-medium">Объект</th>
                <th className="text-left px-4 py-3 font-medium">Статус</th>
                <th className="text-right px-4 py-3 font-medium">Оборот план</th>
                <th className="text-right px-4 py-3 font-medium">Оборот факт</th>
                <th className="text-right px-4 py-3 font-medium">Затраты</th>
                <th className="text-right px-4 py-3 font-medium">Маржа</th>
                <th className="text-right px-4 py-3 font-medium">%</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#212b3d]">
              {data.objects.map((o) => (
                <tr key={o.id} data-testid={`finance-row-${o.id}`} className="hover:bg-[#161d2b]">
                  <td className="px-4 py-3 font-medium text-slate-100">{o.title}<div className="text-[11px] text-slate-500">{o.city}</div></td>
                  <td className="px-4 py-3"><StatusBadge status={o.status} /></td>
                  <td className="px-4 py-3 text-right font-mono">{fmtEur(o.revenue_planned)}</td>
                  <td className="px-4 py-3 text-right font-mono text-emerald-400">{fmtEur(o.revenue_actual)}</td>
                  <td className="px-4 py-3 text-right font-mono text-red-400">{fmtEur(o.actual_cost)}</td>
                  <td className={`px-4 py-3 text-right font-mono ${o.margin >= 0 ? "text-amber-400" : "text-red-400"}`}>{fmtEur(o.margin)}</td>
                  <td className="px-4 py-3 text-right font-mono text-slate-300">{o.margin_pct}%</td>
                </tr>
              ))}
              {data.objects.length === 0 && (
                <tr><td colSpan="7" className="text-center py-10 text-slate-500">Нет данных</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function TotalCard({ label, value, icon: Icon, color }) {
  const map = {
    amber: "text-amber-400 bg-amber-500/10 border-amber-500/30",
    blue: "text-blue-400 bg-blue-500/10 border-blue-500/30",
    emerald: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30",
    red: "text-red-400 bg-red-500/10 border-red-500/30",
  };
  return (
    <div className="kpi-card rounded-xl border border-[#212b3d] bg-[#131924] p-5">
      <div className={`w-10 h-10 rounded-lg border flex items-center justify-center mb-4 ${map[color]}`}><Icon size={18} /></div>
      <div className="text-[11px] uppercase tracking-widest text-slate-500 font-medium mb-1">{label}</div>
      <div className="font-display font-bold text-2xl text-slate-100 leading-none">{value}</div>
    </div>
  );
}
